import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import DocumentPreviewContent from "@/components/DocumentPreviewContent";
import useKalkulatorCatalog from "@/hooks/useKalkulatorCatalog";

const SHEET_WIDTH = 794; // matches DocumentPreviewContent's max-w-[794px] (A4 @ ~96dpi)

// No-login client view — the link sent via the "Kirim ke Client" WhatsApp/Email buttons
// on a document row. Deliberately outside <Protected>: whoever opens this link (the
// client) has no CRM account, so it fetches from the public (unauthenticated) document
// endpoint instead of the normal one, and skips the auth-gated /cetak page entirely.
export default function PublicDocument() {
  const { docId } = useParams();
  const { data: d, isLoading, isError } = useQuery({
    queryKey: ["public-doc", docId],
    queryFn: async () => (await api.get(`/public/documents/${docId}`)).data,
    retry: false,
  });
  const { data: tpl } = useQuery({
    queryKey: ["quotation-template"],
    queryFn: async () => (await api.get("/settings/quotation-template")).data,
  });
  // Public share link — always a brand-new tab/session for the client, so (same as
  // DocumentPrint.jsx) the hardcoded kalkulatorCatalog.js defaults must be overridden with
  // the admin's live catalog before a kalkulator_estimasi document is shown, or the client
  // sees stale class names/prices that no longer match Pengaturan.
  const { isLoading: catalogLoading } = useKalkulatorCatalog();

  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalHeight, setNaturalHeight] = useState(0);

  useEffect(() => {
    const computeScale = () => {
      const available = window.innerWidth - 24;
      setScale(Math.min(1, available / SHEET_WIDTH));
    };
    computeScale();
    window.addEventListener("resize", computeScale);
    return () => window.removeEventListener("resize", computeScale);
  }, []);

  useEffect(() => {
    if (!innerRef.current) return;
    const el = innerRef.current;
    const measure = () => setNaturalHeight(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [d]);

  if (isError) return (
    <div className="min-h-screen bg-white flex items-center justify-center text-zinc-500 text-sm px-6 text-center">
      Dokumen tidak ditemukan atau sudah dihapus.
    </div>
  );
  if (isLoading || !d || !tpl || (d.type === "kalkulator_estimasi" && catalogLoading)) return <div className="min-h-screen bg-white" />;

  return (
    <div className="print-page min-h-screen bg-zinc-200 py-6">
      <style>{`
        @media print {
          html { color-scheme: light!important; }
          html, body, #root { background-color:#fff!important; margin:0!important; }
          * { box-shadow:none!important; text-shadow:none!important; outline:none!important; }
          .no-print{display:none!important}
          .print-page{background:#fff!important;padding:0!important;min-height:0!important}
          .doc-scale-outer{height:auto!important;width:100%!important}
          .doc-scale-inner{transform:none!important;width:100%!important}
          .sheet{border:none!important;outline:none!important;margin:0!important;width:100%!important;max-width:100%!important}
          .page-break{page-break-after:always}
          tr{page-break-inside:avoid}
        }
        @page{size:A4;margin:10mm}
      `}</style>
      <div className="no-print max-w-[794px] mx-auto mb-3 flex justify-end px-2" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button data-testid="doc-print-btn" onClick={() => window.print()} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium min-h-[44px]"><Printer size={16} />Cetak / Simpan PDF</button>
      </div>
      <div className="doc-scale-outer" style={{ width: SHEET_WIDTH * scale, height: naturalHeight ? naturalHeight * scale : undefined, margin: "0 auto", overflow: "hidden" }}>
        <div ref={innerRef} className="doc-scale-inner" style={{ width: SHEET_WIDTH, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <DocumentPreviewContent d={d} tpl={tpl} />
        </div>
      </div>
    </div>
  );
}
